import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from '@tanstack/react-router';
import { Loader2, Filter } from 'lucide-react';
import WineIcon from '@/components/icons/WineIcon';
import WineCard from '@/components/WineCard';
import SharedWineDetail from '@/components/share/SharedWineDetail';
import { IconButton, Heading, MonoLabel, Body, Button } from '@/components/rc';
import { SortMenu } from '@/components/SortMenu';
import { SurfaceProvider } from '@/context/SurfaceContext';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import FilterOverlay from '@/components/FilterOverlay';
import { getShare } from '@/services/shareService';
import { trackEvent } from '@/config/analytics';
import {
  EMPTY_FILTERS,
  matchesAllFacets,
  aggregateFacetOptions,
  countActiveFilters,
  type FiltersState,
  type FacetKey,
  type FacetOption,
} from '@/lib/faceted-filters';
import type { Wine, SharedCellar, SharedWine, SortField } from '@/types';

/** Fill in missing Wine fields so WineCard / filter logic accepts SharedWine data */
function sharedWineToWine(sw: SharedWine): Wine {
  return {
    ...sw,
    location: undefined,
    personalNote: undefined,
    linkToWine: undefined,
    processingStatus: undefined,
    notifiedForYear: undefined,
  } as Wine;
}

function formatExpiryDate(expiresAt: number): string {
  return new Date(expiresAt).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

const FACET_KEYS: readonly FacetKey[] = [
  'wineType', 'maturityStatus', 'country', 'region', 'appellation',
  'producer', 'grapeVariety', 'vintage', 'price',
] as const;

const SharedCellarPage: React.FC = () => {
  const { shareId } = useParams({ from: '/share/$shareId' });
  const [loading, setLoading] = useState(true);
  const [cellar, setCellar] = useState<SharedCellar | null>(null);
  const [error, setError] = useState<'not-found' | 'expired' | 'error' | null>(null);
  const [selectedWine, setSelectedWine] = useState<SharedWine | null>(null);

  // Filter + sort state
  const [filters, setFilters] = useState<FiltersState>(EMPTY_FILTERS);
  const [sortField, setSortField] = useState<SortField>('maturity');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getShare(shareId);
        if (cancelled) return;
        if (!data) {
          setError('not-found');
        } else {
          setCellar(data);
          trackEvent('view_shared_cellar', { wineCount: data.totalLabels });
        }
      } catch {
        if (!cancelled) setError('error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shareId]);

  // Convert SharedWine[] → Wine[] for filter/sort logic
  const allWines = useMemo(
    () => cellar?.wines.map(sharedWineToWine) ?? [],
    [cellar],
  );

  // Apply filters
  const filteredWines = useMemo(
    () => allWines.filter(w => matchesAllFacets(w, filters)),
    [allWines, filters],
  );

  // Apply sort
  const sortedWines = useMemo(() => {
    const sorted = [...filteredWines];
    sorted.sort((a, b) => {
      switch (sortField) {
        case 'maturity': return 0; // keep original order
        case 'vintage-desc': {
          const aV = a.isNV ? 0 : (a.vintage || 0);
          const bV = b.isNV ? 0 : (b.vintage || 0);
          return bV - aV;
        }
        case 'vintage-asc': {
          // NV (0) sorts to the end
          const aV = a.isNV || !a.vintage ? Infinity : a.vintage;
          const bV = b.isNV || !b.vintage ? Infinity : b.vintage;
          return aV - bV;
        }
        case 'rating': return (b.criticRating || 0) - (a.criticRating || 0);
        case 'price-desc': return (b.price || 0) - (a.price || 0);
        case 'price-asc': return (a.price || 0) - (b.price || 0);
        case 'producer': return a.producer.localeCompare(b.producer);
        case 'country': return a.country.localeCompare(b.country);
        default: return 0;
      }
    });
    return sorted;
  }, [filteredWines, sortField]);

  const totalBottlesFiltered = useMemo(
    () => sortedWines.reduce((sum, w) => sum + (Number(w.quantity) || 0), 0),
    [sortedWines],
  );

  // Facet options for filter overlay
  const facetOptions = useMemo(() => {
    const result: Record<string, FacetOption[]> = {};
    for (const key of FACET_KEYS) {
      result[key] = aggregateFacetOptions(allWines, filters, key);
    }
    return result;
  }, [allWines, filters]);

  const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters]);

  const toggleFacet = useCallback((key: FacetKey, value: string) => {
    setFilters(prev => {
      const facet = prev[key];
      if ('include' in facet) {
        const arr = facet.include;
        const next = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
        return { ...prev, [key]: { ...facet, include: next } };
      }
      return prev;
    });
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setSortField('maturity');
  }, []);

  if (loading) {
    return (
      <div className="min-h-dvh bg-[var(--rc-surface-tertiary)] text-[var(--rc-ink-primary)] flex flex-col items-center justify-center gap-4">
        <Loader2 className="animate-spin text-[var(--rc-accent-pink)]" size={48} />
        <p className="font-mono text-sm animate-pulse uppercase tracking-widest">Loading...</p>
      </div>
    );
  }

  if (error || !cellar) {
    return (
      <div className="min-h-dvh bg-[var(--rc-surface-tertiary)] text-[var(--rc-ink-primary)] flex flex-col items-center justify-center gap-6 px-6 text-center">
        <WineIcon size={64} className="text-[var(--rc-ink-ghost)]" />
        <Heading scale="title">
          {error === 'expired' ? 'This shared cellar has expired.' : 'Shared cellar not found.'}
        </Heading>
        <Body colour="ghost">
          {error === 'expired'
            ? 'Shared links expire after 30 days.'
            : "This link may have expired or doesn't exist."}
        </Body>
        <a href="/">
          <Button variantType="Primary" label="EXPLORE RAVE CAVE" />
        </a>
      </div>
    );
  }

  return (
    <SurfaceProvider>
      <div className="fixed inset-0 overflow-y-auto overflow-x-hidden overscroll-none bg-[var(--rc-surface-tertiary)] text-[var(--rc-ink-primary)]">
        {/* Header */}
        <div className="p-4 sm:p-10 space-y-4 sm:space-y-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6">
            <div className="flex-1 w-full">
              <div className="flex items-center gap-3 mb-2">
                <WineIcon size={32} className="text-[var(--rc-accent-pink)]" />
                <MonoLabel size="label" colour="accent-acid" className="w-auto uppercase tracking-widest">
                  Shared View
                </MonoLabel>
              </div>
              <Heading scale="title">{cellar.ownerDisplayName}&apos;s Collection</Heading>
              <MonoLabel size="label" colour="ghost" className="mt-1">
                Expires {formatExpiryDate(cellar.expiresAt)}
              </MonoLabel>
            </div>

            <div className="flex gap-4 sm:gap-6 items-end flex-shrink-0">
              <div className="flex flex-col items-end">
                <span className="text-[var(--rc-accent-pink)] text-4xl sm:text-6xl font-display leading-none tracking-tight">
                  {totalBottlesFiltered}
                </span>
                <span className="text-[9px] sm:text-xs font-mono uppercase tracking-widest text-[var(--rc-ink-primary)]">
                  Bottles
                </span>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-[var(--rc-accent-acid)] text-4xl sm:text-6xl font-display leading-none tracking-tight text-stroke-black">
                  {sortedWines.length}
                </span>
                <span className="text-[9px] sm:text-xs font-mono uppercase tracking-widest text-[var(--rc-ink-primary)]">
                  Labels
                </span>
              </div>
              <div className="flex gap-2 items-center self-center">
                <div className="relative">
                  <IconButton icon={Filter} aria-label="Filter" onClick={() => setMobileFiltersOpen(true)} />
                  {activeFilterCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-[var(--rc-ink-primary)] text-[var(--rc-accent-acid)] font-[var(--rc-font-mono)] text-[9px] font-bold">
                      {activeFilterCount}
                    </span>
                  )}
                </div>
                <SortMenu value={sortField} onChange={setSortField} iconButton />
              </div>
            </div>
          </div>

          {/* Wine Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-8 pb-24 sm:pb-20">
            {sortedWines.map(wine => (
              <WineCard
                key={wine.id}
                wine={wine}
                readOnly
                onClick={() => {
                  const shared = cellar.wines.find(w => w.id === wine.id);
                  if (shared) setSelectedWine(shared);
                }}
              />
            ))}
            {sortedWines.length === 0 && (
              <div className="col-span-full py-20 text-center">
                <Body colour="ghost">No wines match the current filters.</Body>
                <button onClick={clearFilters} className="mt-3">
                  <MonoLabel size="label" colour="accent-pink" className="w-auto cursor-pointer hover:underline">
                    Clear filters
                  </MonoLabel>
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="text-center pb-8">
            <a
              href="/"
              className="inline-flex items-center gap-2 opacity-40 hover:opacity-70 transition-opacity"
            >
              <WineIcon size={16} className="text-[var(--rc-accent-pink)]" />
              <MonoLabel size="micro" colour="ghost">Powered by Rave Cave</MonoLabel>
            </a>
          </div>
        </div>

        {/* Filter BottomSheet */}
        <BottomSheet
          open={mobileFiltersOpen}
          onOpenChange={setMobileFiltersOpen}
          snapPoint="full"
          snapPoints={['full']}
          id="shared-filters"
          title="Filters"
          dismissible
        >
          <FilterOverlay
            filters={filters}
            facetOptions={facetOptions}
            filteredCount={sortedWines.length}
            onToggleFacet={toggleFacet}
            onClearFilters={clearFilters}
            onClose={() => setMobileFiltersOpen(false)}
            currency="USD"
          />
        </BottomSheet>

        {/* Wine Detail Modal */}
        {selectedWine && (
          <SharedWineDetail wine={selectedWine} onClose={() => setSelectedWine(null)} />
        )}
      </div>
    </SurfaceProvider>
  );
};

export default SharedCellarPage;
